import { type Customer } from "./customers";

export const BOOKINGS_QUERY_KEY = "bookings";

export const BOOKING_STATUS_MAP = {
  scheduled: {
    label: "Scheduled",
    color: "red",
  },
  expected: {
    label: "Expected",
    color: "amber",
  },
  noshow: {
    label: "No-Show",
    color: "red",
  },
  checkedin: {
    label: "Checked In",
    color: "green",
  },
  arrived: {
    label: "Arrived",
    color: "amber",
  },
  unknown: {
    label: "Unknown",
    color: "gray2",
  },
  seasonal: {
    label: "Seasonal",
    color: "orange",
  },
  storage: {
    label: "Storage",
    color: "gray2",
  },
  cancelled: {
    label: "Cancelled",
    color: "gray2",
  },
  departed: {
    label: "Departed",
    color: "purple",
  },
  overstayed: {
    label: "Over-stayed",
    color: "red",
  },
  disallowed: {
    label: "Disallowed",
    color: "red",
  },
} as const;

// Keep in sync with BookingStatus in bookings.py
export type BookingStatus = keyof typeof BOOKING_STATUS_MAP;

/** The barrier will not open for it: the Disallow checkbox, or a vehicle on the Disallow List. */
export const isBookingDisallowed = (booking: {
  disallow: boolean;
  onDisallowList: boolean;
}): boolean => booking.disallow || booking.onDisallowList;

export type Pitch = {
  id: string;
  name: string;
  pitchTypeName: string;
  // Experience Freedom units are Pitches under an EF-defaulting PitchType,
  // so this is how glamping units are told apart from touring pitches.
  isExperienceFreedom: boolean;
  mapX: number;
  mapY: number;
  currentBookingId?: string;
  currentBookingRef?: string;
  currentBookingName?: string;
  currentBookingStatus?: BookingStatus;
  currentBookingStart?: string; // ISO date (stay start)
  currentBookingEnd?: string; // ISO date (stay end)
  currentBookingArrivedAt?: string | null; // ISO datetime, when they arrived on site
  currentBookingCheckoutAt?: string | null; // ISO datetime, checkout deadline
};

export type PitchType = {
  id: string;
  name: string;
  siteId: string;
  colorRgb: string;
};

// Keep in sync with VehicleBookingSchema
export type VehicleBooking = {
  id: string;
  registration: string;
  isPrimary: boolean;
  type: string | null;
  length: string | null;
  trailerLength: string | null;
  // On the Disallow List (only on vehicles read from the API).
  onDisallowList?: boolean;
};

// Keep in sync with StorageSummarySchema: a storage booking's compound pitch
// and where its unit is.
export type BookingStorageSummary = {
  pitchId: string;
  pitchName: string;
  kind: "caravan" | "motorhome" | "trailer_tent" | "other";
  location: "in" | "out";
};

// Keep in sync with BookingSchema
export type BookingApiResponseBase = {
  id: string;
  bookingRef: string;
  balanceDue: number;
  autoBalance: boolean;
  zeroAndArriveUsed: boolean;
  isCompanion: boolean;
  startDate: string;
  endDate: string;
  arrivalStart: string | null;
  arrivalEnd: string | null;
  lateArrivalStart: string | null;
  departureEnd: string | null;
  overrideArrivalStart: string | null;
  overrideArrivalEnd: string | null;
  overrideLateArrivalStart: string | null;
  overrideDepartureEnd: string | null;
  manuallyDeparted: boolean;
  arrivedAt: string | null;
  checkedInAt: string | null;
  departedAt: string | null;
  siteId: string;
  note: string | null;
  customer: Customer;
  vehicleBookings: VehicleBooking[];
  status: BookingStatus;
  hasAlert: boolean;
  disallow: boolean;
  // A vehicle is on the Disallow List (live; `disallow` is the per-booking checkbox).
  onDisallowList: boolean;
};

// Where the booking came from. The importers own `cancelled` on feed bookings,
// so those cannot be cancelled from the dashboard.
export type BookingSource =
  | "manual"
  | "travellink"
  | "experience_freedom"
  | "pitchup";

export const FEED_BOOKING_SOURCES: readonly BookingSource[] = [
  "travellink",
  "experience_freedom",
  "pitchup",
];

// Every booking (Experience Freedom included) resolves to a Pitch / PitchType.
// An Experience Freedom "unit" is just the booking's `pitch` (a Pitch of an experience_freedom PitchType).
export type BookingApiResponse = BookingApiResponseBase & {
  bookingType:
    | "default"
    | "storage"
    | "seasonal"
    | "experience_freedom"
    | "ferry"
    | "pitchup";
  pitchType: string;
  pitchNumber: string | null;
  pitch: Pitch | null;
  reserved: Pitch | null;
  source: BookingSource | null;
  storage: BookingStorageSummary | null;
};

export type BookingBase = {
  id: string;
  bookingRef: string;
  balanceDue: number;
  autoBalance: boolean;
  isCompanion: boolean;
  zeroAndArriveUsed: boolean;
  siteId: string;
  startDate: string;
  endDate: string;
  arrivalStart: string | null;
  arrivalEnd: string | null;
  lateArrivalStart: string | null;
  departureEnd: string | null;
  overrideArrivalStart: string | null;
  overrideArrivalEnd: string | null;
  overrideLateArrivalStart: string | null;
  overrideDepartureEnd: string | null;
  manuallyDeparted: boolean;
  customer: Customer;
  vehicleBookings: VehicleBooking[];
  primaryVehicleBooking: VehicleBooking | null;
  arrivedAt: string | null;
  checkedInAt: string | null;
  departedAt: string | null;
  note: string;
  disallow: boolean;
  onDisallowList: boolean;
  status: BookingStatus;
  hasAlert: boolean;
};

export type Booking = BookingBase & {
  bookingType:
    | "default"
    | "storage"
    | "seasonal"
    | "experience_freedom"
    | "ferry"
    | "pitchup";
  pitchType: string;
  pitchNumber: string | null;
  pitch: Pitch | null;
  reserved: Pitch | null;
  source: BookingSource | null;
  storage: BookingStorageSummary | null;
};

export const parseBookingApiResponse = (
  apiResponse: BookingApiResponse,
): Booking => {
  const primaryVehicleBooking =
    apiResponse.vehicleBookings.find(({ isPrimary }) => isPrimary) ?? null;
  const secondaryVehicleBookings = apiResponse.vehicleBookings.filter(
    ({ isPrimary }) => !isPrimary,
  );

  return {
    ...apiResponse,
    customer: {
      ...apiResponse.customer,
      title: apiResponse.customer.title ?? "",
      firstName: apiResponse.customer.firstName ?? "",
      lastName: apiResponse.customer.lastName ?? "",
    },
    primaryVehicleBooking,
    vehicleBookings: secondaryVehicleBookings,
    note: apiResponse.note ?? "",
  };
};
